import React from 'react';
import { motion } from 'framer-motion';
import { Code2, Check, ArrowRight, ArrowLeft, Palette, Smartphone, Zap, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import useMetaTags from '../hooks/useMetaTags';
import useStructuredData from '../hooks/useStructuredData';
import { NavbarHTAG } from '../components/NavbarHTAG';
import { FooterHTAG } from '../components/FooterHTAG';

export const DesignWebService = () => {
  // Meta tags SEO
  useMetaTags({
    title: 'Design & Création Web en Ardennes | HTAG Facility',
    description: 'Création de sites web modernes, responsifs et performants en Ardennes. Design web sur mesure, e-commerce, refonte. Basé à Château-Porcien. Assistance numérique intégrée.',
    keywords: 'design web, création site internet, web design, e-commerce, responsive design, Ardennes, Château-Porcien',
    url: 'https://htagfacility.fr/services/design-web',
    image: 'https://htagfacility.fr/og-service-design.png',
    type: 'article'
  });

  // Structured Data - Service
  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Design & Création Web Sur Mesure',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'HTAG Facility',
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': 'Château-Porcien',
        'addressLocality': 'Château-Porcien',
        'addressRegion': 'Ardennes',
        'postalCode': '08320',
        'addressCountry': 'FR'
      }
    },
    'description': 'Création de sites web modernes, responsifs et optimisés pour la conversion',
    'areaServed': ['FR-08', 'Ardennes', 'Grand Est'],
    'offers': {
      '@type': 'Offer',
      'description': 'Service de design web sur mesure'
    }
  });

  return (
    <div className="bg-[#181C25] text-white lg:ml-16">
      <NavbarHTAG />

      {/* Bouton retour */}
      <div className="relative z-10">
        <div className="container mx-auto mb-8">
          <Link
            to="/?scroll=services"
            className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors font-semibold hover:translate-x-1 duration-300"
          >
            <ArrowLeft size={18} />
            <span>Retour aux services</span>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pb-24 bg-gradient-to-b from-[#212635] to-[#181C25]">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            {/* Contenu */}
            <div>
              <h1 className="text-5xl lg:text-6xl font-montserrat font-bold mb-6">
                Design & Web <span className="text-cyan-400">sur mesure</span>
              </h1>
              <p className="text-xl text-gray-300 mb-4 leading-relaxed">
                Créez une présence web impactante qui reflète votre marque et convertit vos visiteurs en clients.
              </p>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                De la conception visuelle époustouflante au développement technique robuste, nous construisons des sites web qui performent et engage votre audience. <strong className="text-gray-300">Création en visio avec rencontres sur site si souhaité.</strong>
              </p>
              <a
                href="/?scroll=contact"
                className="inline-flex items-center space-x-3 px-8 py-4 rounded-lg bg-cyan-600 hover:bg-cyan-700 font-montserrat font-semibold transition-all"
              >
                <span>Démarrer un projet</span>
                <ArrowRight size={20} />
              </a>
            </div>

            {/* Visuel */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative h-96 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-2 border-cyan-500/30 overflow-hidden"
            >
              <img 
                src="https://images.unsplash.com/photo-1561070791-2526d30994b5?w=500&h=500&fit=crop" 
                alt="Design Web" 
                className="w-full h-full object-cover opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#181C25] to-transparent flex items-end justify-center pb-6">
                <p className="text-gray-300 font-montserrat text-lg font-semibold">Création Web Moderne</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services détails - Spécialités */}
      <section className="py-24">
        <div className="container mx-auto">
          <h2 className="text-4xl font-montserrat font-bold mb-16 text-white text-center">
            Nos spécialités web et design
          </h2>
          
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-8 mb-24"
          >
            {[
              {
                title: 'Création de sites web',
                icon: Code2,
                description: 'Sites modernes, responsive et performants adaptés à votre secteur d\'activité.',
                items: ['Design responsive & mobile-first', 'Développement frontend/backend', 'Optimisation SEO intégrée', 'Performance et sécurité', 'Intégration CMS ou eCommerce'],
              },
              {
                title: 'Refonte & modernisation',
                icon: Smartphone,
                description: 'Donnez un coup de jeune à votre présence web existante.',
                items: ['Audit et diagnostic complet', 'Identification des améliorations', 'Migration sans perte de données', 'Redirection intelligente des URLs', 'Formation aux nouveaux outils'],
              },
              {
                title: 'Design & Branding',
                icon: Palette,
                description: 'Une identité visuelle cohérente et mémorable sur tous vos supports.',
                items: ['Création de logo & charte graphique', 'Supports print & digital', 'Stratégie de couleurs & typographie', 'Guides de marque complets', 'Templates réutilisables'],
              },
              {
                title: 'Optimisationweb',
                icon: Zap,
                description: 'Améliorez la performance, le référencement et la conversion de votre site.',
                items: ['Optimisation SEO on-page & technique', 'Amélioration vitesse chargement', 'Tests A/B et analytics', 'Optimisation conversion (CTA, formulaires)', 'Suivi et rapports mensuels'],
              },
            ].map((service, idx) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="spotlight-card p-8 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 hover:border-cyan-500 transition-all"
                >
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-lg bg-cyan-600 flex items-center justify-center mr-4">
                      <Icon size={24} />
                    </div>
                    <h3 className="font-montserrat font-bold text-xl text-white">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-gray-300 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.items.map((item, i) => (
                      <li key={i} className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" />
                        <span className="text-gray-300 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Section avec image - Processus */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid lg:grid-cols-2 gap-12 items-center mb-24"
          >
            <div className="relative h-96 rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=500&h=500&fit=crop" 
                alt="Processus" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#181C25] to-transparent"></div>
            </div>
            
            <div>
              <h2 className="text-4xl font-montserrat font-bold mb-8 text-white">
                Notre processus de création
              </h2>
              <div className="space-y-6">
                {[
                  { step: 1, title: 'Découverte', desc: 'Entretien approfondi sur vos besoins, votre cible et vos objectifs commerciaux' },
                  { step: 2, title: 'Wireframing & Design', desc: 'Création de maquettes visuelles et structure UX validées par vos soins' },
                  { step: 3, title: 'Développement', desc: 'Codage selon les derniers standards web et best practices du secteur' },
                  { step: 4, title: 'Tests & Optimisation', desc: 'Tests complets, optimisation performance, sécurité et accessibilité' },
                  { step: 5, title: 'Lancement & Support', desc: 'Mise en ligne, formation et support continu post-lancement' },
                ].map((process, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="p-6 rounded-lg bg-gradient-to-r from-cyan-500/10 to-transparent border border-cyan-500/20"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 rounded-full bg-cyan-600 flex items-center justify-center flex-shrink-0 font-montserrat font-bold">
                        {process.step}
                      </div>
                      <div>
                        <h4 className="font-montserrat font-bold text-white text-lg">
                          {process.title}
                        </h4>
                        <p className="text-gray-400 text-sm mt-1">{process.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Technologies & Outils */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-24"
          >
            <h2 className="text-4xl font-montserrat font-bold mb-12 text-white text-center">
              Technos & Outils modernes
            </h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { cat: 'Frontend', items: ['React', 'Vue.js', 'Tailwind CSS', 'JavaScript', 'Responsive Design'] },
                { cat: 'Backend', items: ['Node.js', 'PHP/Laravel', 'Bases de données', 'APIs REST', 'Sécurité'] },
                { cat: 'CMS & eCommerce', items: ['WordPress', 'Shopify', 'Webflow', 'Wix', 'Custom Solutions'] },
                { cat: 'Outils Design', items: ['Figma', 'Adobe XD', 'Photoshop', 'Illustrator', 'Wireframing'] },
              ].map((tech, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-6 rounded-lg bg-gradient-to-br from-cyan-600/20 to-transparent border border-cyan-500/30"
                >
                  <h3 className="font-montserrat font-bold text-cyan-400 mb-4">{tech.cat}</h3>
                  <ul className="space-y-2">
                    {tech.items.map((item, i) => (
                      <li key={i} className="text-gray-300 text-sm flex items-center space-x-2">
                        <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Service en Ardennes */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mb-24 p-12 rounded-2xl bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30"
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="text-green-400" size={28} />
                  <h3 className="text-2xl font-montserrat font-bold text-white">
                    Design Web en Ardennes & Région Grand Est
                  </h3>
                </div>
                <p className="text-gray-300 mb-4">
                  HTAG Facility crée des sites web modernes pour PME, associations et collectivités en Ardennes et région Grand Est. Basé à <strong>Château-Porcien</strong>, nous accompagnons votre projet digital de la conception à la publication.
                </p>
                <p className="text-gray-400 text-sm">
                  <strong>Processus collaboratif :</strong> Ateliers de travail en visio ou sur site. Assistance continue après le lancement.
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className="text-center p-6 rounded-xl bg-green-600/10 border border-green-500/30">
                  <p className="text-green-400 font-montserrat font-bold text-sm mb-2">SERVICE RÉGIONAL</p>
                  <p className="text-white font-bold text-lg">Ardennes</p>
                  <p className="text-gray-400 text-sm">Grand Est, France</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA Final */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center p-12 rounded-2xl bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border border-cyan-500/30"
          >
            <h3 className="text-3xl font-montserrat font-bold mb-4 text-white">
              Votre projet web commence ici
            </h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Parlons de votre vision. Eric créera une solution web unique, performante et adaptée à votre stratégie commerciale.
            </p>
            <a
              href="/?scroll=contact"
              className="inline-flex items-center space-x-3 px-10 py-4 rounded-lg bg-cyan-600 hover:bg-cyan-700 font-montserrat font-semibold transition-all"
            >
              <span>Discuter de votre projet</span>
              <ArrowRight size={20} />
            </a>
          </motion.div>
        </div>
      </section>

      <FooterHTAG />
    </div>
  );
};

export default DesignWebService;
